import type { DesktopConfig } from ".";
import {
  APP_REGISTRY,
  DEFAULT_DESKTOP_ICON_POSITIONS,
  DEFAULT_MOBILE_ICON_POSITIONS,
} from "./apps/registry";
import {
  AboutDemo,
  AboutIcon,
  AboutTitleIcon,
  BrowserIcon,
  BrowserTitleIcon,
  CalculatorDemo,
  CalculatorIcon,
  CalculatorTitleIcon,
  ClockDemo,
  ClockIcon,
  ClockTitleIcon,
  ContactDemo,
  ContactIcon,
  ContactTitleIcon,
  ExperienceDemo,
  ExperienceIcon,
  ExperienceTitleIcon,
  ProjectsDemo,
  ProjectsIcon,
  ProjectsTitleIcon,
  ResumeDemo,
  ResumeIcon,
  ResumeTitleIcon,
  SettingsDemo,
  SettingsIcon,
  SettingsTitleIcon,
  SkillsDemo,
  SkillsIcon,
  SkillsTitleIcon,
} from "./components/apps";

/* --------------------------------------------------------------------------
   Desktop configuration.

   The windows[] and icons[] arrays must stay in lockstep with APP_REGISTRY —
   the drift guard at the bottom throws at module load if they diverge.
   -------------------------------------------------------------------------- */

const demoConfig: DesktopConfig = {
  windows: [
    {
      id: "about",
      type: "app",
      title: "About Me",
      icon: <AboutTitleIcon />,
      component: AboutDemo,
      dimensions: {
        responsive: false,
        width: "820px",
        height: "560px",
        dimensionPercentage: { width: 46, height: 68 },
        minWidth: 560,
        minHeight: 460,
        position: "center",
      },
    },
    {
      id: "resume",
      type: "app",
      title: "Résumé",
      icon: <ResumeTitleIcon />,
      component: ResumeDemo,
      dimensions: {
        responsive: false,
        width: "900px",
        height: "640px",
        position: "center",
      },
    },
    {
      id: "experience",
      type: "app",
      title: "Experience",
      icon: <ExperienceTitleIcon />,
      component: ExperienceDemo,
      dimensions: {
        responsive: false,
        width: "760px",
        height: "560px",
        position: "center",
      },
    },
    {
      id: "skills",
      type: "app",
      title: "Skills",
      icon: <SkillsTitleIcon />,
      component: SkillsDemo,
      dimensions: {
        responsive: false,
        width: "760px",
        height: "560px",
        position: "center",
      },
    },
    {
      id: "projects",
      type: "app",
      title: "Projects",
      icon: <ProjectsTitleIcon />,
      component: ProjectsDemo,
      dimensions: {
        responsive: false,
        width: "820px",
        height: "600px",
        position: "center",
      },
    },
    {
      id: "contact",
      type: "app",
      title: "Contact",
      icon: <ContactTitleIcon />,
      component: ContactDemo,
      dimensions: {
        responsive: false,
        width: "560px",
        height: "480px",
        position: "center",
      },
    },
    {
      id: "settings",
      type: "app",
      title: "Settings",
      icon: <SettingsTitleIcon />,
      component: SettingsDemo,
      dimensions: {
        responsive: false,
        width: "720px",
        height: "540px",
        position: "center",
      },
    },
    {
      id: "calculator",
      type: "app",
      title: "Calculator",
      icon: <CalculatorTitleIcon />,
      component: CalculatorDemo,
      dimensions: {
        responsive: false,
        width: "360px",
        height: "540px",
        position: "center",
      },
    },
    {
      id: "stopwatch",
      type: "app",
      title: "Clock",
      icon: <ClockTitleIcon />,
      component: ClockDemo,
      dimensions: {
        responsive: false,
        width: "420px",
        height: "520px",
        position: "center",
      },
    },
    {
      id: "browser",
      type: "browser",
      title: "Browser",
      icon: <BrowserTitleIcon />,
      // Many sites block embedding via X-Frame-Options; this Google embed URL
      // is a known-embeddable placeholder. Point it at an embeddable portfolio
      // target later.
      url: "https://www.google.com/search?igu=1",
      showReloadButton: true,
      openMaximized: false,
      dimensions: {
        responsive: false,
        width: "900px",
        height: "600px",
        position: "center",
      },
    },
  ],
  icons: [
    {
      id: "about-icon",
      windowId: "about",
      icon: <AboutIcon />,
      label: "About Me",
      dockPriority: 10,
    },
    {
      id: "resume-icon",
      windowId: "resume",
      icon: <ResumeIcon />,
      label: "Résumé",
      dockPriority: 9,
    },
    {
      id: "experience-icon",
      windowId: "experience",
      icon: <ExperienceIcon />,
      label: "Experience",
      dockPriority: 8,
    },
    {
      id: "skills-icon",
      windowId: "skills",
      icon: <SkillsIcon />,
      label: "Skills",
      dockPriority: 7,
    },
    {
      id: "projects-icon",
      windowId: "projects",
      icon: <ProjectsIcon />,
      label: "Projects",
      dockPriority: 6,
    },
    {
      id: "contact-icon",
      windowId: "contact",
      icon: <ContactIcon />,
      label: "Contact",
      dockPriority: 5,
    },
    {
      id: "settings-icon",
      windowId: "settings",
      icon: <SettingsIcon />,
      label: "Settings",
      dockPriority: 4,
    },
    {
      id: "calculator-icon",
      windowId: "calculator",
      icon: <CalculatorIcon />,
      label: "Calculator",
      dockPriority: 3,
    },
    {
      id: "stopwatch-icon",
      windowId: "stopwatch",
      icon: <ClockIcon />,
      label: "Clock",
      dockPriority: 2,
    },
    {
      id: "browser-icon",
      windowId: "browser",
      icon: <BrowserIcon />,
      label: "Browser",
      dockPriority: 1,
    },
  ],
  darkBackground: {
    type: "image",
    url: "https://images.pexels.com/photos/34720596/pexels-photo-34720596.jpeg?auto=compress&cs=tinysrgb&w=1920",
    size: "cover",
    position: "center",
  },
  lightBackground: {
    type: "image",
    url: "https://images.pexels.com/photos/691668/pexels-photo-691668.jpeg?auto=compress&cs=tinysrgb&w=1920",
    size: "cover",
    position: "center",
  },
  iconLayout: {
    startPosition: "dock",
    direction: "horizontal", // ignored for dock
    gap: 8,
    padding: 8,
  },
  defaultIconPositions: {
    desktop: DEFAULT_DESKTOP_ICON_POSITIONS,
    mobile: DEFAULT_MOBILE_ICON_POSITIONS,
  },
  autoOpenWindow: "about",
};

/* --------------------------------------------------------------------------
   Drift guard: every APP_REGISTRY entry must have a matching window in the
   config, and every window's id must be a known registry windowId. Runs once
   at module load — add an app to the registry without wiring it here (or vice
   versa) and it crashes loud rather than silently 404'ing on open.
   -------------------------------------------------------------------------- */

(function assertConfigMatchesRegistry() {
  const configWindowIds = new Set<string>(demoConfig.windows.map((w) => w.id));
  const registryWindowIds = new Set<string>(
    Object.values(APP_REGISTRY).map((a) => a.windowId)
  );
  const missingFromConfig = [...registryWindowIds].filter(
    (id) => !configWindowIds.has(id)
  );
  const unknownInConfig = [...configWindowIds].filter(
    (id) => !registryWindowIds.has(id)
  );
  if (missingFromConfig.length || unknownInConfig.length) {
    throw new Error(
      `desktop-config drift vs APP_REGISTRY:\n` +
        (missingFromConfig.length
          ? `  registry has [${missingFromConfig.join(", ")}] but no matching window\n`
          : "") +
        (unknownInConfig.length
          ? `  config has window(s) [${unknownInConfig.join(", ")}] not in registry\n`
          : "")
    );
  }
  const configIconIds = new Set<string>(demoConfig.icons.map((i) => i.id));
  const registryIconIds = new Set<string>(
    Object.values(APP_REGISTRY)
      .map((a): string | undefined => ("iconId" in a ? a.iconId : undefined))
      .filter((id): id is string => typeof id === "string")
  );
  const missingIcons = [...registryIconIds].filter(
    (id) => !configIconIds.has(id)
  );
  if (missingIcons.length) {
    throw new Error(
      `desktop-config drift: registry has iconId(s) [${missingIcons.join(", ")}] but no matching icon entry`
    );
  }
})();

export default demoConfig;
